import {
  Box,
  Flex,
  Heading,
  HStack,
  Skeleton,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
} from '@chakra-ui/react';
import React, { useMemo } from 'react';

import { LoansFiltersDrawer } from 'packages/loans/ui/LoansFiltersDrawer';
import { LoansTable } from 'packages/loans/ui/LoansTable';
import { SummaryCard } from 'packages/loans/ui/SummaryCard';
import { useCreditFacilitiesOrThrow } from '~/contexts/CreditFacilitiesProvider';
import { useFeatureFlags } from '~/contexts/FeatureFlagProvider';
import { dashboardRoutePadding } from '~/packages/common-frontend/constants';
import { useLoansFilters } from '~/packages/loans/hooks/useLoansFilters';
import {
  useGetSummarizedLoansQuery,
  SummarizedLoanFragmentGQLType,
  useGetAggregateLoansMetadataQuery,
} from '~/packages/loans/requests/loans.gql.generated';

export const LoansOverview = () => {
  const { productsLoansEditPermissions } = useFeatureFlags();
  const { facilities } = useCreditFacilitiesOrThrow();

  // Filters
  const { filters, setFilters } = useLoansFilters({
    creditFacilityIds: facilities.map((facility) => facility.id),
  });

  // TODO: restore after other performance improvements in accounting querying
  const showAggregateMetadata = false;

  const aggregateMetadataQuery = useGetAggregateLoansMetadataQuery({
    skip: !showAggregateMetadata,
    fetchPolicy: 'network-only',
    variables: {
      filterParams: {
        ...filters,
        isFinal: true,
      },
    },
  });
  if (aggregateMetadataQuery.error) throw aggregateMetadataQuery.error;

  const summarizedLoansQuery = useGetSummarizedLoansQuery({
    fetchPolicy: 'network-only',
    variables: {
      filterParams: {
        ...filters,
        isFinal: true,
      },
    },
  });
  if (summarizedLoansQuery.error) throw summarizedLoansQuery.error;

  const loansWithPendingEdits = useMemo<SummarizedLoanFragmentGQLType[]>(() => {
    if (!summarizedLoansQuery.data?.loans) {
      return [];
    }

    return summarizedLoansQuery.data.loans.filter((loan) => {
      return loan.edits.destination?.loanAction.status === 'submitted';
    });
  }, [summarizedLoansQuery.data]);

  const {
    totalOutstandingBalance,
    totalAccruedInterest,
    totalAccruedServiceFees,
  } = aggregateMetadataQuery.data?.results ?? {};

  return (
    <Box padding={dashboardRoutePadding}>
      <HStack justify="space-between">
        <Heading>Loans</Heading>

        <HStack gap="1.5rem">
          {showAggregateMetadata && aggregateMetadataQuery.loading ? (
            <Skeleton h="3rem" w="10rem" />
          ) : (
            <LoansFiltersDrawer
              onFilterChange={setFilters}
              initialFilters={filters}
            />
          )}
        </HStack>
      </HStack>
      {showAggregateMetadata && (
        <Flex sx={{ gap: '1rem', flexWrap: 'wrap', width: '100%', mt: '2rem' }}>
          <SummaryCard
            loading={aggregateMetadataQuery.loading}
            name="Total Principal"
            value={
              typeof totalOutstandingBalance === 'number'
                ? totalOutstandingBalance
                : '-'
            }
          />
          <SummaryCard
            loading={aggregateMetadataQuery.loading}
            name="Total Accrued Interest & Fees"
            value={
              typeof totalAccruedInterest === 'number' ||
              typeof totalAccruedServiceFees === 'number'
                ? (totalAccruedInterest ?? 0) + (totalAccruedServiceFees ?? 0)
                : '-'
            }
          />
        </Flex>
      )}

      {/* //TODO: add loans chart / visuals @markhaghani */}
      {productsLoansEditPermissions !== 'allow' ? (
        <LoansTable
          loading={summarizedLoansQuery.loading}
          loans={summarizedLoansQuery.data?.loans ?? []}
        />
      ) : (
        <Tabs>
          <TabList>
            <Tab>All</Tab>
            <Tab>Pending Edits</Tab>
          </TabList>
          <TabPanels>
            <TabPanel p={0}>
              <LoansTable
                loading={summarizedLoansQuery.loading}
                loans={summarizedLoansQuery.data?.loans ?? []}
              />
            </TabPanel>
            <TabPanel p={0}>
              <LoansTable
                loading={summarizedLoansQuery.loading}
                loans={loansWithPendingEdits}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      )}
    </Box>
  );
};
